import { useCallback } from 'react';
import { CANVAS_PAD, PAGE_GAP, usePagePreviewScale } from '../shared/usePagePreviewScale';
import PagePreviewPages from '../shared/PagePreviewPages';
import VoterInclusionFormPreview, {
    VOTER_INCLUSION_PAGE_HEIGHT,
    VOTER_INCLUSION_PAGE_WIDTH,
} from './VoterInclusionFormPreview';

/** The printed form is one application, its authority page and the declaration. */
const PAGE_COUNT = 3;
const DOCUMENT_HEIGHT = VOTER_INCLUSION_PAGE_HEIGHT * PAGE_COUNT + PAGE_GAP * (PAGE_COUNT - 1);

/**
 * Both pages of the form inside the sticky preview rail.
 *
 * The paper is scaled to fit the card rather than scrolled around — a form you
 * have to pan to read is no use as a preview — so a whole page is on screen at
 * once and scrolling moves to the next one. Zoomed in past the card, it is
 * dragged around instead.
 */
export default function VoterInclusionPreviewFrame({ form, previewRef, zoom = 1 }) {
    const [scrollRef, scale, page] = usePagePreviewScale(
        VOTER_INCLUSION_PAGE_WIDTH,
        VOTER_INCLUSION_PAGE_HEIGHT,
        zoom,
        PAGE_COUNT,
    );

    const assignPreviewRef = useCallback(
        (node) => {
            if (typeof previewRef === 'function') {
                previewRef(node);
            } else if (previewRef) {
                previewRef.current = node;
            }
        },
        [previewRef],
    );

    return (
        // The page marker is pinned to the card, not to the paper, so it stays
        // put while the pages scroll under it.
        <div className="relative flex min-h-0 w-full flex-1">
            <div
                ref={scrollRef}
                className="w-full min-h-0 flex-1 select-none overflow-auto rounded-lg bg-[#3d3d3d]"
                style={{ padding: CANVAS_PAD }}
            >
                {/* The scaled paper is laid out at full size, so the scroll box is
                    given the height the transform actually paints. */}
                <div
                    style={{
                        height: DOCUMENT_HEIGHT * scale,
                        width: VOTER_INCLUSION_PAGE_WIDTH * scale,
                        margin: '0 auto',
                    }}
                >
                    <div
                        className="shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
                        style={{
                            width: VOTER_INCLUSION_PAGE_WIDTH,
                            transform: `scale(${scale})`,
                            transformOrigin: 'top left',
                        }}
                    >
                        <VoterInclusionFormPreview ref={assignPreviewRef} form={form} />
                    </div>
                </div>
            </div>
            <PagePreviewPages page={page} total={PAGE_COUNT} />
        </div>
    );
}
